import {
    formatPercentage,
    formatGroupedInteger,
    formatInteger,
    formatVolume,
    getDecimalNumber,
    getFormattedDate,
    centupleDecimalFormatter,
    formatBlankEmptyInteger,
    formatBlankEmptyVolume,
    blankEmptyFormatPercentage,
    blankEmptyFormatPercentageWithMultiplier,
    formatOneDecimalPercentageWithMultiplier,
    formatStandardPct,
    boolToShortString,
    formatBlankEmptyUnit
} from '../../../../agent-production/helpers/dataFormatters';

export const unitWidth = Object.freeze({
    icon: 40,
    dom: 80,
    tiny: 92,
    small: 100,
    compact: 120,
    regular: 155,
    moderate: 180,
    medium: 230,
    mediumLarge: 275,
    large: 330,
    extraLarge: 520,
});

export const defaultColumnDef = {
    flex: 1,
    sortingOrder: ['desc', 'asc'],
    suppressHeaderMenuButton: true,
    sortable: true
};

const iconDefaults = {
    cellClass: 'd-flex justify-content-center align-items-center pr-0',
    minWidth: unitWidth.icon,
    resizable: false
}

const textDefaults = {
    headerClass: 'font-weight-bold',
    minWidth: unitWidth.regular,
    resizable: true
};

const textDefaultsRightAlign = {
    headerClass: 'align-top no-wrap td-num d-flex justify-content-end',
    cellClass: 'td-num',
    minWidth: unitWidth.regular,
    resizable: true
};

const numberDefaults = {
    cellClass: 'td-num',
    headerClass: 'align-top no-wrap td-num d-flex justify-content-end',
    minWidth: unitWidth.regular,
    resizable: true
};

const checkboxRightAlign = {
    cellClass: 'checkbox-right-align',
    headerClass: 'font-weight-bold',
    maxWidth: 34,
    resizable: false,
};

export const dividerClassNames = {
    dividerCol: 'divider-col',
    leftDividerCol: 'left-divider-col',
    transparentResizeIcon: 'transparent-resize-icon'
};

export const colSettings = {
    textTinyRightAlign: {
        ...textDefaultsRightAlign,
        minWidth: 55
    },
    icon: {
        ...iconDefaults
    },
    textTiny: {
        ...textDefaults,
        minWidth: 55
    },
    textXs: {
        ...textDefaults,
        minWidth: unitWidth.tiny
    },
    textXsRightAlign: {
        ...textDefaultsRightAlign,
        minWidth: unitWidth.tiny
    },
    textSmall: {
        ...textDefaults,
        minWidth: unitWidth.compact
    },
    textSmRightAlign: {
        ...textDefaultsRightAlign,
        minWidth: unitWidth.compact
    },
    textSm: {
        ...textDefaults
    },
    textMd: {
        ...textDefaults,
        minWidth: unitWidth.medium
    },
    textLg: {
        ...textDefaults,
        minWidth: unitWidth.large
    },
    textXl: {
        ...textDefaults,
        minWidth: unitWidth.extraLarge
    },
    numberXsInteger: {
        ...numberDefaults,
        minWidth: unitWidth.tiny,
        valueFormatter: v => formatInteger(v.value)
    },
    numberSmRightAlign: {
        ...numberDefaults
    },
    numberSmTiny: {
        ...numberDefaults,
        minWidth: 55,
        maxWidth: 55,
        valueFormatter: v => formatInteger(v.value)
    },
    numberSmInteger: {
        ...numberDefaults,
        valueFormatter: v => formatInteger(v.value)
    },
    numberMdInteger: {
        ...numberDefaults,
        minWidth: unitWidth.moderate,
        valueFormatter: v => formatInteger(v.value)
    },
    numberLgInteger: {
        ...numberDefaults,
        minWidth: unitWidth.medium,
        valueFormatter: v => formatInteger(v.value)
    },
    numberMdLgInteger: {
        ...numberDefaults,
        minWidth: unitWidth.mediumLarge,
        valueFormatter: v => formatInteger(v.value)
    },
    numberXsPercentage: {
        ...numberDefaults,
        minWidth: unitWidth.small,
        valueFormatter: v => formatPercentage(v.value)
    },
    numberSmPercentage: {
        ...numberDefaults,
        valueFormatter: v => formatPercentage(v.value)
    },
    numberSmPercentageMultiplier: {
        ...numberDefaults,
        valueFormatter: v => formatOneDecimalPercentageWithMultiplier(v.value)
    },
    numberMdPercentage: {
        ...numberDefaults,
        minWidth: unitWidth.moderate,
        valueFormatter: v => formatPercentage(v.value)
    },
    numberSmGroupedInteger: {
        ...numberDefaults,
        valueFormatter: v => formatGroupedInteger(v.value)
    },
    blankEmptyNumberSmPercentage: {
        ...numberDefaults,
        valueFormatter: v => blankEmptyFormatPercentage(v.value)
    },
    numberSmCentuplePercentage: {
        ...numberDefaults,
        valueFormatter: v => centupleDecimalFormatter(v.value)
    },
    blankEmptyNumberSmPercentageWithMultiplier: {
        ...numberDefaults,
        valueFormatter: v => blankEmptyFormatPercentageWithMultiplier(v.value)
    },
    numberXsVolume: {
        ...numberDefaults,
        minWidth: unitWidth.tiny,
        valueFormatter: v => formatVolume(v.value)
    },
    numberSmVolume: {
        ...numberDefaults,
        valueFormatter: v => formatVolume(v.value)
    },
    numberMdVolume: {
        ...numberDefaults,
        minWidth: unitWidth.moderate,
        valueFormatter: v => formatVolume(v.value)
    },
    numberLgVolume: {
        ...numberDefaults,
        minWidth: unitWidth.medium,
        valueFormatter: v => formatVolume(v.value)
    },
    blankEmptyTinyVolume: {
        ...numberDefaults,
        minWidth: unitWidth.tiny,
        valueFormatter: v => formatBlankEmptyVolume(v.value)
    },
    blankEmptyXsInteger: {
        ...numberDefaults,
        minWidth: 104,
        valueFormatter: v => formatBlankEmptyInteger(v.value)
    },
    blankEmptyXsVolume: {
        ...numberDefaults,
        minWidth: 104,
        valueFormatter: v => formatBlankEmptyVolume(v.value)
    },
    blankEmptyXsUnit: {
        ...numberDefaults,
        minWidth: 104,
        valueFormatter: v => formatBlankEmptyUnit(v.value)
    },
    numberMdLgVolume: {
        ...numberDefaults,
        minWidth: unitWidth.mediumLarge,
        valueFormatter: v => formatVolume(v.value)
    },
    numberExLgVolume: {
        ...numberDefaults,
        minWidth: unitWidth.large,
        valueFormatter: v => formatVolume(v.value)
    },
    blankEmptySmVolume : {
        ...numberDefaults,
        valueFormatter: v => formatBlankEmptyVolume(v.value)
    },
    numberSmUnits: {
        ...numberDefaults,
        valueFormatter: v => getDecimalNumber(v.value)
    },
    numberMdUnits: {
        ...numberDefaults,
        minWidth: unitWidth.medium,
        valueFormatter: v => getDecimalNumber(v.value)
    },
    numberMdLgUnits: {
        ...numberDefaults,
        minWidth: unitWidth.mediumLarge,
        valueFormatter: v => getDecimalNumber(v.value)
    },
    numberLgUnits: {
        ...numberDefaults,
        minWidth: unitWidth.large,
        valueFormatter: v => getDecimalNumber(v.value)
    },
    dateSm: {
        ...textDefaults,
        valueFormatter: v => getFormattedDate(v.value, 'yyyy-MM-dd')
    },
    dateMd: {
        ...textDefaults,
        minWidth: unitWidth.moderate,
        valueFormatter: v => getFormattedDate(v.value, 'yyyy-MM-dd')
    },
    numberLgPercentageOneDecimal:{
        ...numberDefaults,
        minWidth: unitWidth.medium,
        valueFormatter: v => formatOneDecimalPercentageWithMultiplier(v.value)
    },
    bankStatus:{
        ...textDefaults,
        minWidth: unitWidth.moderate,
        valueFormatter: v => boolToShortString(v.value)
    },
    numberTwoDecimalsPercentage: {
        ...numberDefaults,
        valueFormatter: v => formatStandardPct(v.value)
    },
    checkboxSmall: {
        maxWidth: 34,
        resizable: false,
    },
    checkboxMedium: {
        maxWidth: 50,
        resizable: false,
    },
    checkboxSmallRight: {
        ...checkboxRightAlign,
        maxWidth: 34,
    },
};
